import type { AppStore } from './app';
import type { SubmitAnswerResponse } from '../services/api';
import type { FeedbackStars } from '../types/feedback';
import type { AxiosResponse } from 'axios';
import { makeAutoObservable, reaction } from 'mobx';

import { i18n } from '../configs/i18n';
import { parseApiError } from '../services/api-error';
import {
  type Answer,
  type Question,
  type QuestionButton,
  QuestionType,
  type UpdateQuestionType,
} from '../types/question';
import { wait } from '../utils/wait';


type RawQuestionButton = {
  callback_data: string;
  text: string;
};

const transformButtons = (buttons: RawQuestionButton[]): QuestionButton[] =>
  buttons.map(({ callback_data, text }) => ({
    text,
    value: callback_data,
  }));

export class QuestionStore {
  initialData: Question = {
    text: 'Ожидайте начала игры',
    type: 0,
    id: '',
  };

  fetching = false;
  data = this.initialData;
  message: string | null = null;
  error: string | null = null;
  answer: Answer | null = null;
  ignoreFetch = false;
  delay = 0;
  isSubmitted = false;

  private delayTimer: ReturnType<typeof setTimeout> | null = null;
  /** id вопроса, на который ушёл текущий submit — чтобы не блокировать уже сменившийся вопрос. */
  private pendingSubmitId: string | null = null;

  constructor(private appStore: AppStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get type() {
    return this.data.type;
  }

  get token() {
    if (!this.appStore.token) {
      throw new Error('Token is required');
    }

    return this.appStore.token;
  }

  fetch = async () => {
    const startedAt = Date.now();
    const { data } = await this.appStore.api.getQuestion(this.token);
    const finishedAt = Date.now();
    const fetchingTime = finishedAt - startedAt;
    const minFetchingTime = 150;

    // TOOD: move animation duration to question component
    if (fetchingTime < minFetchingTime) {
      await wait(minFetchingTime - fetchingTime);
    }

    const previousId = this.data.id;
    const questionChanged = data.id !== previousId;

    if (questionChanged) {
      this.setAnswer(null);
      this.setMessage(null);
      this.setSubmitted(false);
    }

    this.setData(data);

    if (data.disabled) {
      this.setSubmitted(true);
    } else if (questionChanged) {
      this.setSubmitted(false);
    }
  };

  loadQuestion = async () => {
    try {
      this.setFetching(true);
      this.setError(null);
      await this.fetch();
    } catch (error) {
      this.setError(i18n.t('question.error.fetch'));
    } finally {
      this.setFetching(false);
    }
  };

  finishQuestion = async () => {
    try {
      this.setFetching(true);

      if (this.appStore.isCaptain && !this.data.disabled && !this.isSubmitted) {
        switch (this.type) {
          case QuestionType.Text:
            if (this.answer?.text) {
              await this.submitTextAnswer(this.answer.text, this.data.id);
            }
            break;
          case QuestionType.TextWithRate:
            if (this.answer?.text && this.answer.stake !== null) {
              await this.submitRateAnswer(
                Number(this.answer.stake),
                this.answer.text,
                this.data.id,
              );
            }
            break;
        }
      }

      await this.fetch();
    } catch (error) {
      console.log(error);
      this.setError(i18n.t('question.error.fetch'));
    } finally {
      this.setFetching(false);
    }
  };

  setData(data: Question) {
    const result = data;
    // это ненужно, если контракт будет правильный
    if (
      data.type === QuestionType.OptionsInteractive &&
      data.buttons?.length &&
      'callback_data' in (data.buttons[0] as object)
    ) {
      result.buttons = transformButtons(data.buttons as unknown as RawQuestionButton[]);
    }

    this.data = result;
  }

  setFetching(value: boolean) {
    this.fetching = value;
  }

  setError(value: string | null) {
    this.error = value;
  }

  setMessage(value: string | null) {
    this.message = value;
  }

  setAnswer = (value: Answer | null) => {
    this.answer = value
      ? {
          ...this.answer,
          ...value,
        }
      : value;
  };

  setSubmitted = (value: boolean) => (this.isSubmitted = value);

  setIgnoreFetch(value: boolean) {
    this.ignoreFetch = value;
  }

  startSubmitFlow(isOnlyForCaptain = true) {
    return new Promise<null>((resolve, reject) => {
      this.pendingSubmitId = this.data.id || null;
      this.restoreInitialState();

      if (isOnlyForCaptain && !this.appStore.isCaptain) {
        reject(new Error(i18n.t('question.error.captain')));
      }

      this.setFetching(true);
      resolve(null);
    });
  }

  isStaleSubmit() {
    const submittedId = this.pendingSubmitId;
    return Boolean(submittedId && this.data.id && submittedId !== this.data.id);
  }

  handleSubmitResponse(response: AxiosResponse<SubmitAnswerResponse>) {
    if (this.isStaleSubmit()) {
      this.pendingSubmitId = null;
      return;
    }
    this.pendingSubmitId = null;
    const { text } = response.data;
    this.setMessage(text || i18n.t('question.success'));
    this.setSubmitted(true);
  }

  handleInteractiveSubmitResponse(response: AxiosResponse<SubmitAnswerResponse>) {
    const { text, question, finalize } = response.data;
    if (question) {
      this.setIgnoreFetch(true);
      this.setData(question);
      this.setIgnoreFetch(false);
    }
    if (finalize) {
      this.setSubmitted(true);
      this.setMessage(text || i18n.t('question.success'));
      return;
    }
    // buy / backspace — форма остаётся открытой
    this.setSubmitted(false);
    if (text) {
      this.setMessage(text);
    }
  }

  catchSubmitError(error: unknown) {
    if (this.isStaleSubmit()) {
      this.pendingSubmitId = null;
      return;
    }
    this.pendingSubmitId = null;
    const apiError = parseApiError(error);
    if (apiError.question) {
      this.setIgnoreFetch(true);
      this.setData(apiError.question);
      this.setIgnoreFetch(false);
      this.setSubmitted(false);
    }
    this.setError(apiError.message || i18n.t('question.error.unknown'));
  }

  finishSubmitFlow() {
    this.setFetching(false);
  }

  submitTextAnswer = (value: string, id?: string) => {
    return this.startSubmitFlow()
      .then(() => this.appStore.api.submitAnswer(this.token, this.data.type, value, id))
      .then(this.handleSubmitResponse)
      .catch(this.catchSubmitError)
      .finally(this.finishSubmitFlow);
  };

  submitButtonsAnswer = (value: string) => {
    const selectedText = this.data.buttons?.find((button) => button.value === value)?.text;

    return this.startSubmitFlow()
      .then(() => this.appStore.api.submitButtons(this.token, this.data.type, value))
      .then((response) => {
        this.handleSubmitResponse(response);
        if (selectedText) {
          this.setData({ ...this.data, value: selectedText });
          this.setAnswer({ text: selectedText, stake: null });
          this.updateInputText({ text: selectedText, stake: null });
        }
      })
      .catch(this.catchSubmitError)
      .finally(this.finishSubmitFlow);
  };

  submitInteractiveAnswer = (value: string) => {
    return this.startSubmitFlow()
      .then(() => this.appStore.api.submitInteractive(this.token, this.data.type, value))
      .then((response) => {
        this.handleInteractiveSubmitResponse(response);
      })
      .catch(this.catchSubmitError)
      .finally(this.finishSubmitFlow);
  };

  submitRateAnswer = (stake: number | null, text: string, id?: string) => {
    if (stake === null) {
      this.setError(i18n.t('question.error.rate'));
      return null;
    }

    return this.startSubmitFlow()
      .then(() => this.appStore.api.submitAnswer(this.token, this.data.type, text, id, stake))
      .then(this.handleSubmitResponse)
      .catch(this.catchSubmitError)
      .finally(this.finishSubmitFlow);
  };

  submitFeedback = (stars: FeedbackStars, text: string) => {
    return this.startSubmitFlow(false)
      .then(() =>
        this.appStore.api.submitFeedback(
          this.token,
          this.appStore.gameData?.user_token || '',
          stars,
          text,
        ),
      )
      .then(this.handleSubmitResponse)
      .catch(this.catchSubmitError)
      .finally(this.finishSubmitFlow);
  };

  handleUpdateQuestion = (response: UpdateQuestionType) => {
    const delaySec = Math.max(0, Number(response?.delay) || 0);
    const autosubmit = Boolean(response?.autosubmit);
    if (this.ignoreFetch) {
      return;
    }

    if (this.delayTimer != null) {
      clearTimeout(this.delayTimer);
      this.delayTimer = null;
    }

    const proceed = autosubmit ? this.finishQuestion : this.loadQuestion;

    if (delaySec > 0) {
      this.setDelay(delaySec);
      this.delayTimer = setTimeout(() => {
        this.delayTimer = null;
        this.clearDelay();
        void proceed();
      }, delaySec * 1000);
      return;
    }

    this.clearDelay();
    void proceed();
  };

  handleReconnect = () => {
    if (this.delay > 0) {
      return;
    }
    void this.fetch();
  };

  updateInputText = ({ text, stake }: Answer) => {
    if (!this.appStore.isCaptain) {
      return;
    }

    this.appStore.socket.emit('updateAnswerText', {
      text,
      stake,
      token: this.appStore.token,
    });
  };

  restoreInitialState() {
    this.setError(null);
    this.setMessage(null);
    this.setSubmitted(false);
  }

  cleanUp = () => {
    if (this.delayTimer != null) {
      clearTimeout(this.delayTimer);
      this.delayTimer = null;
    }
    this.appStore.socket.off('updateAnswerText');
    this.appStore.socket.off('updateQuestion');
    this.appStore.socket.io.off('reconnect', this.handleReconnect);
  };

  start = () => {
    this.loadQuestion();

    if (!this.appStore.isCaptain) {
      this.appStore.socket.on('updateAnswerText', this.setAnswer);
    }
    this.appStore.socket.on('updateQuestion', this.handleUpdateQuestion);

    this.appStore.socket.io.on('reconnect', this.handleReconnect);

    reaction(
      () => this.appStore.isCaptain,
      (isCaptain) => {
        if (isCaptain) {
          this.appStore.socket.off('updateAnswerText');
        } else {
          this.appStore.socket.on('updateAnswerText', this.setAnswer);
        }
      },
    );
  };

  setDelay(value: number) {
    this.delay = value;
  }

  clearDelay = () => {
    this.setDelay(0);
  };
}
